import { useEffect, useState } from 'react';

import { useSession } from '../auth/SessionContext';
import { apiSend } from '../lib/api';
import { Button } from '../ui';

/**
 * The persistent banner for a creator whose address is not confirmed yet.
 *
 * Signing in no longer waits for the confirmation link — with no email
 * provider configured a stranger could never read it — so the address is
 * asked for here instead, on every page, until it is confirmed. The two
 * things it gates are named in the sentence, because "confirm your email" on
 * its own reads as nagging and "you cannot submit a clip until you do" reads
 * as a reason.
 *
 * Resend is rate-limited by the API (per address and per IP, 204 either way,
 * so the button can never be used to ask whether an address exists) and held
 * for a minute here after a press, so a person who clicks four times does
 * not spend their hour's allowance on one inbox.
 */
const RESEND_HOLD_SECONDS = 60;

export function VerifyEmailBanner(): React.JSX.Element | null {
  const { session, refresh } = useSession();
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [hold, setHold] = useState(0);

  useEffect(() => {
    if (hold <= 0) return;
    const timer = window.setTimeout(() => setHold((h) => h - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [hold]);

  if (!session || session.user.role !== 'CREATOR' || session.user.emailVerified) return null;
  const email = session.user.email;

  async function resend(): Promise<void> {
    setState('sending');
    // Always 204, whatever the address: this cannot be read as an answer to
    // "does that account exist".
    await apiSend('POST', '/auth/resend-verification', { email }).catch(() => undefined);
    setState('sent');
    setHold(RESEND_HOLD_SECONDS);
  }

  return (
    <div className="alert verify-banner" data-tone="waiting" role="status">
      <strong>Confirm your email to connect accounts, submit clips and request payouts.</strong>
      <span className="alert-body">
        <span>
          We sent a link to <strong>{email}</strong>. Nothing there? Check your spam folder, or
          send it again.
          {state === 'sent' && ' A new link is on its way.'}
        </span>
        <span className="actions">
          <Button
            small
            onClick={() => void resend()}
            disabled={state === 'sending' || hold > 0}
          >
            {state === 'sending'
              ? 'Sending…'
              : hold > 0
                ? `Sent — try again in ${hold}s`
                : 'Resend the email'}
          </Button>
          {/* For the person who confirmed in another tab: the banner does not
              know until the session is re-read. */}
          <Button small onClick={() => void refresh()}>
            I have confirmed it
          </Button>
        </span>
      </span>
    </div>
  );
}
